import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    createStyles,
    makeStyles,
    Theme,
} from '@bb-ui/react-library/dist/components/styles';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { Link } from '@bb-ui/react-library/dist/components/Link';
import { Attention } from '@bb-ui/icons/dist/medium/Attention';
import { CopyButton } from '../../components/CopyButton/CopyButton';
import { useHelpLinks } from '../../hooks/useHelpLinks';
import { AccountNotProvisionedDetail } from './authFailureDetail';

// Shown when the identity provider authenticated the user but they have no Illuminate
// account. The user has to relay these details to an administrator, so the screen sets
// no time limit and offers a copy control. Built from bb-ui primitives rather than the
// shared Message component, which renders its text as a paragraph and cannot host the
// heading focus has to land on.

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: theme.spacing(3),
            textAlign: 'center',
        },
        heading: {
            marginTop: theme.spacing(2),
            // Focus target on mount; keeps the default bb-ui indicator.
            outlineOffset: '2px',
        },
        body: {
            marginTop: theme.spacing(1),
            maxWidth: '40rem',
        },
        details: {
            marginTop: theme.spacing(3),
            textAlign: 'start',
        },
        detailValue: {
            // User data of unknown direction, isolated so it cannot reorder the label.
            unicodeBidi: 'isolate',
        },
        account: {
            display: 'block',
        },
        actions: {
            alignItems: 'center',
            display: 'flex',
            // Wraps rather than crowding on a narrow viewport.
            flexWrap: 'wrap',
            gap: theme.spacing(3),
            justifyContent: 'center',
            marginTop: theme.spacing(3),
        },
        // Lets the copy button and its status line wrap in the actions row.
        copy: {
            display: 'contents',
        },
        status: {
            // Own line under every button.
            flexBasis: '100%',
            order: 1,
            // Reserves the line so the confirmation does not shift the buttons.
            minHeight: theme.spacing(3),
        },
    }),
);

export interface AccountNotProvisionedProps {
    detail: AccountNotProvisionedDetail;
    children?: React.ReactNode;
}

export const AccountNotProvisioned: React.FC<
    AccountNotProvisionedProps
> = props => {
    const { detail, children } = props;
    const classes = useStyles();
    const { t } = useTranslation();
    const getHelpLink = useHelpLinks();
    const headingRef = React.useRef<HTMLHeadingElement>(null);

    React.useEffect(() => {
        headingRef.current?.focus();
    }, []);

    // The admin enters the login name. The email or name, when different, only
    // helps the user recognise the account: a minor line under the username.
    const account =
        detail.loginName && detail.username !== detail.loginName
            ? {
                  label: t('general.accountNotProvisionedAccountLabel'),
                  value: detail.username,
              }
            : undefined;
    const fields = [
        {
            key: 'username',
            label: t('general.accountNotProvisionedUsernameLabel'),
            value: detail.loginName ?? detail.username,
        },
        {
            key: 'idp',
            label: t('general.accountNotProvisionedProviderLabel'),
            value: detail.idp,
        },
    ].filter(field => field.value);

    // Copied text follows the on-screen order: Username, Account, Provider.
    const copyText = fields
        .flatMap(field =>
            field.key === 'username' && account ? [field, account] : [field],
        )
        .map(field => `${field.label}: ${field.value}`)
        .join('\n');

    return (
        <div
            className={classes.container}
            data-testid="account-not-provisioned"
        >
            <Attention />
            <Typography
                variant="h4"
                component="h1"
                color="error"
                className={classes.heading}
                ref={headingRef}
                tabIndex={-1}
            >
                {t('general.accountNotProvisionedHeading')}
            </Typography>
            {/* Named tags rather than positional <1>/<3>: prettier inserts {' '}
                when it wraps JSX text before an element, which silently shifts
                positional indices. The children below are the fallback used when the
                key is unavailable. */}
            <Typography component="p" className={classes.body}>
                <Trans
                    i18nKey="general.accountNotProvisionedBody"
                    components={{
                        brand: <span lang="en" />,
                        helplink: (
                            <Link
                                href={getHelpLink('addOrDeleteUsers')}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={t(
                                    'general.accountNotProvisionedHelpLinkLabel',
                                )}
                            />
                        ),
                    }}
                >
                    Your <span lang="en">Blackboard Illuminate</span> account
                    isn&apos;t set up yet. Give these details to your
                    administrator so they can{' '}
                    <Link
                        href={getHelpLink('addOrDeleteUsers')}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t(
                            'general.accountNotProvisionedHelpLinkLabel',
                        )}
                    >
                        set it up
                    </Link>
                    .
                </Trans>
            </Typography>

            {fields.length > 0 && (
                <dl className={classes.details}>
                    {fields.map(field => (
                        <React.Fragment key={field.key}>
                            <Typography component="dt" variant="subtitle2">
                                {field.label}
                            </Typography>
                            <Typography
                                component="dd"
                                dir="auto"
                                className={classes.detailValue}
                            >
                                {field.value}
                                {field.key === 'username' && account && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        component="span"
                                        className={classes.account}
                                        data-testid="account-not-provisioned-account"
                                    >
                                        {account.label}:{' '}
                                        <bdi>{account.value}</bdi>
                                    </Typography>
                                )}
                            </Typography>
                        </React.Fragment>
                    ))}
                </dl>
            )}

            <div className={classes.actions}>
                {fields.length > 0 && (
                    <CopyButton
                        text={copyText}
                        label={t('general.accountNotProvisionedCopy')}
                        successMessage={t(
                            'general.accountNotProvisionedCopySuccess',
                        )}
                        failureMessage={t(
                            'general.accountNotProvisionedCopyFailure',
                        )}
                        testId="copy"
                        className={classes.copy}
                        statusClassName={classes.status}
                    />
                )}
                {children}
            </div>
        </div>
    );
};
